import { duration } from "common";
import { isEmpty, isPlainObject, omit, pickBy } from "lodash";

export const LAST_ACTIVITY_KEY = "xola.auth.lastActivityAt";
export const IDLE_LIMIT_KEY = "xola.auth.idleLimitMinutes";

const WRITE_THROTTLE_MS = duration(15, "seconds");

// Locked-down browsers can block storage; this keeps the feature working in a single tab.
const memory: Record<string, string | null> = { [LAST_ACTIVITY_KEY]: null, [IDLE_LIMIT_KEY]: null };

const readRaw = (key: string): string | null => {
    try {
        return localStorage.getItem(key);
    } catch {
        return memory[key];
    }
};

const writeRaw = (key: string, value: string | null) => {
    memory[key] = value;
    try {
        if (value === null) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
    } catch {
        // Memory already holds it.
    }
};

const read = (key: string): number | null => {
    const value = readRaw(key);
    const parsed = Number(value);
    return value !== null && parsed > 0 ? parsed : null;
};

const write = (key: string, value: number | null) => writeRaw(key, value === null ? null : String(value));

export const readLastActivity = () => read(LAST_ACTIVITY_KEY);

export const markActivity = () => {
    const now = Date.now();
    const lastActivityAt = readLastActivity();
    if (lastActivityAt === null || now - lastActivityAt >= WRITE_THROTTLE_MS) {
        write(LAST_ACTIVITY_KEY, now);
    }
};

interface SellerIdleLimit {
    minutes: number;
    seenAt: number;
}

// A seller closed for 5 minutes stops counting; long enough to outlast a throttled background tab's checks.
const STALE_LIMIT_MS = duration(5, "minutes");

const isFresh = (limit: SellerIdleLimit | undefined, now: number) => now - (limit?.seenAt ?? 0) < STALE_LIMIT_MS;

const readLimits = (): Record<string, SellerIdleLimit> => {
    try {
        const limits = JSON.parse(readRaw(IDLE_LIMIT_KEY) ?? "{}");
        return isPlainObject(limits) ? limits : {};
    } catch {
        return {};
    }
};

// One browser is one session: every tab follows the strictest limit among the sellers open in it.
export const readIdleLimit = () => {
    const now = Date.now();
    const limits = Object.values(readLimits())
        .filter((limit) => isFresh(limit, now))
        .map(({ minutes }) => minutes);
    return limits.length > 0 ? Math.min(...limits) : null;
};

// Each seller keeps its own entry, so loosening or turning off the setting reaches tabs that are already signed in.
export const setSellerIdleLimit = (sellerId: string, minutes: number | null) => {
    const now = Date.now();
    const limits = pickBy(readLimits(), (limit) => isFresh(limit, now));
    const current = limits[sellerId];
    const isUnchanged = (current?.minutes ?? null) === minutes;
    if (isUnchanged && (minutes === null || now - current.seenAt < WRITE_THROTTLE_MS)) {
        return;
    }

    const nextLimits = minutes === null ? omit(limits, sellerId) : { ...limits, [sellerId]: { minutes, seenAt: now } };
    writeRaw(IDLE_LIMIT_KEY, isEmpty(nextLimits) ? null : JSON.stringify(nextLimits));
};

export const clearIdleSession = () => {
    write(LAST_ACTIVITY_KEY, null);
    write(IDLE_LIMIT_KEY, null);
};

// Live values belong to other open tabs; only values that timed out, or that no open tab still refreshes, are stale.
export const clearExpiredIdleSession = () => {
    const lastActivityAt = readLastActivity();
    const limitMinutes = readIdleLimit();
    if (lastActivityAt === null) {
        return;
    }

    const isStale = limitMinutes === null || lastActivityAt + duration(limitMinutes, "minutes") <= Date.now();
    if (isStale) {
        clearIdleSession();
    }
};
